// The Skill Builder summary strip on the ADD ACTIVITY page.
//
// Four figures, in the order a tutor reads them: what exists, what this student
// already has, how much of that is done, and what needs them personally.
//
// Only the last one carries colour. Colour belongs on the report, where the
// strip is the headline of the page; here it sits above a picker the tutor is
// working in, and a row of pastel blocks competes with the list for attention.
// "Needs your review" is the exception because it is the one figure that is a
// call to action rather than a status.
//
// The report's version is BmdSkillbuilderKpiCards. The two show different
// figures on purpose, but both read the same `counts` object off one response,
// so they cannot disagree about a number they both show.
//
// Deliberately NOT here any more:
//
//   "With a skill builder"        a second column that, whenever the content is
//                                 complete, repeats the first one exactly.
//   "N needing teaching first"    the FUNDAMENTAL misconceptions, which are not
//                                 remediated by a worksheet at all. It read as
//                                 a warning about the number beside it.
//
// Both were dropped at the PM's request. The figures still come back on the
// response, so restoring either is a display change and nothing more.

import React from "react"
import type { BmdSkillbuilderCounts } from "../types/bmd-skillbuilder.types"
import { BMD_COLOURS } from "../constants/bmd.colours"

const INK = BMD_COLOURS.INK
// A step darker than they started. The QA kept reporting the small grey text
// across the Add Activity tabs as unreadable, so both moved together and the
// sibling tabs match BmdPrereqTabPanel. MUTED is still a clear step off INK,
// so the states that use it to mean "absent" or "disabled" still read that way.
const BODY = BMD_COLOURS.BODY
const MUTED = BMD_COLOURS.MUTED
const RULE = "#e9edf3"
const REVIEW = "#b0862f"

type Props = {
  counts?: BmdSkillbuilderCounts | null
  passPercent: number
  className?: string
}

type StatProps = {
  label: string
  value: number
  unit: string
  tone?: "default" | "review"
}

const Stat = ({ label, value, unit, tone = "default" }: StatProps) => (
  <div style={{ flex: "1 1 0", minWidth: 150, padding: "0 4px" }}>
    <div
      style={{
        color: MUTED,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div className="d-flex align-items-baseline gap-2 mt-1">
      <span
        style={{
          // The review figure only goes amber when there IS something to
          // review; a coloured zero is an alarm about nothing.
          color: tone === "review" && value > 0 ? REVIEW : INK,
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {value}
      </span>
      <span style={{ color: BODY, fontSize: 12 }}>{unit}</span>
    </div>
  </div>
)

export function BmdSkillbuilderStats({
  counts,
  passPercent,
  className,
}: Props) {
  const assigned = counts?.assigned ?? 0
  return (
    <div
      className={`d-flex flex-wrap align-items-start ${className || ""}`}
      style={{
        backgroundColor: "#fff",
        border: `1px solid ${RULE}`,
        borderRadius: 10,
        padding: "16px 20px",
      }}
    >
      {/* The count of WORKSHEETS, not of failed skills. Those differ only when
          a skill has no worksheet authored yet, and a column headed "skill
          builders" has to be the number of skill builders. */}
      <Stat
        label="Available"
        value={counts?.with_skillbuilder ?? 0}
        unit="skill builders"
      />
      <div style={{ width: 1, alignSelf: "stretch", backgroundColor: RULE }} />
      <Stat label="Assigned" value={assigned} unit="to this student" />
      <div style={{ width: 1, alignSelf: "stretch", backgroundColor: RULE }} />
      {/* Out of what was ASSIGNED, not out of what exists: a worksheet the
          tutor never set cannot be counted as one the student has not done. */}
      <Stat
        label="Completed"
        value={counts?.completed ?? 0}
        unit={`of ${assigned}`}
      />
      <div style={{ width: 1, alignSelf: "stretch", backgroundColor: RULE }} />
      <Stat
        label="Needs your review"
        value={counts?.needs_tutor ?? 0}
        unit={`scored under ${passPercent}%`}
        tone="review"
      />
    </div>
  )
}

export default BmdSkillbuilderStats
